import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
	throw new Error(
		"Missing Supabase environment variables. Please check your .env file.",
	);
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
	auth: {
		autoRefreshToken: true,
		persistSession: true,
		detectSessionInUrl: true,
	},
});

// Auth helper functions
export const auth = {
	signUp: async (email: string, password: string, userData: any) => {
		const { data, error } = await supabase.auth.signUp({
			email,
			password,
			options: {
				data: userData,
			},
		});
		return { data, error };
	},

	signIn: async (email: string, password: string) => {
		const { data, error } = await supabase.auth.signInWithPassword({
			email,
			password,
		});
		return { data, error };
	},

	signOut: async () => {
		const { error } = await supabase.auth.signOut();
		return { error };
	},

	getCurrentUser: async () => {
		const {
			data: { user },
			error,
		} = await supabase.auth.getUser();
		return { user, error };
	},

	getSession: async () => {
		const {
			data: { session },
			error,
		} = await supabase.auth.getSession();
		return { session, error };
	},
};

// Database helper functions
export const db = {
	// Users
	createUser: async (userData: any) => {
		const { data, error } = await supabase
			.from("users")
			.insert([userData])
			.select()
			.single();
		return { data, error };
	},

	getUserByAuthId: async (authUserId: string) => {
		const { data, error } = await supabase
			.from("users")
			.select("*")
			.eq("auth_user_id", authUserId)
			.single();
		return { data, error };
	},

	updateUser: async (userId: string, updates: any) => {
		const { data, error } = await supabase
			.from("users")
			.update(updates)
			.eq("id", userId)
			.select()
			.single();
		return { data, error };
	},

	// Doula Listings
	getActiveDoulaListings: async () => {
		const { data, error } = await supabase
			.from("doula_listings")
			.select(
				`
        *,
        user:users(*),
        packages(*),
        certifications:doula_certifications(*),
        experience:doula_experience(*)
      `,
			)
			.eq("status", "active")
			.order("created_at", { ascending: false });
		return { data, error };
	},

	getDoulaListingById: async (id: string) => {
		const { data, error } = await supabase
			.from("doula_listings")
			.select(
				`
        *,
        user:users(*),
        packages(*),
        certifications:doula_certifications(*),
        experience:doula_experience(*)
      `,
			)
			.eq("id", id)
			.single();
		return { data, error };
	},

	createDoulaListing: async (listingData: any) => {
		const { data, error } = await supabase
			.from("doula_listings")
			.insert([listingData])
			.select()
			.single();
		return { data, error };
	},

	updateDoulaListing: async (id: string, updates: any) => {
		const { data, error } = await supabase
			.from("doula_listings")
			.update(updates)
			.eq("id", id)
			.select()
			.single();
		return { data, error };
	},
};
